import { Box } from '@mui/material';
import { EMPTY_DISPLAY_VALUE } from 'common/constants/cap-table';
import { decimalFormat } from 'common/formats/formats';
import theme from 'theme';
import HelpTip from '../HelpTip';

// Set per-cell via `style`, not ScalarSpreadsheet's `columnWidths`/`columnTypes` props -
// those spread the cell into a plain object (gridColumnTransformers.tsx), dropping the
// Cell class's prototype methods and breaking the next edit on that cell.
export const LABEL_COLUMN_WIDTH = 230;
export const VALUE_COLUMN_WIDTH = 140;

// Baked into grid row 1 (not a separate MUI <Table>) so its width always matches the
// data rows below - two independently-styled tables rarely stay pixel-aligned.
const headerCellStyle = (align: 'left' | 'right') => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.primary.contrastText,
  fontWeight: 700,
  fontSize: '0.8rem',
  textAlign: align,
  width: align === 'left' ? LABEL_COLUMN_WIDTH : VALUE_COLUMN_WIDTH,
  minWidth: align === 'left' ? LABEL_COLUMN_WIDTH : VALUE_COLUMN_WIDTH,
  maxWidth: align === 'left' ? LABEL_COLUMN_WIDTH : VALUE_COLUMN_WIDTH,
});

export const sectionHeaderRow = (label: string, valueLabel = 'Value') => ({
  A1: {
    readOnly: true,
    value: label,
    gridType: 'string' as const,
    style: headerCellStyle('left'),
  },
  B1: {
    readOnly: true,
    value: valueLabel,
    gridType: 'string' as const,
    style: headerCellStyle('right'),
  },
});

// Column-A "label" cells render as plain read-only string cells (no gridType), so the
// blanket `.data-grid td { text-align: right }` rule would right-align them like every
// number cell - override to left explicitly per cell via inline style (wins over the
// non-!important class rule).
const LABEL_STYLE = {
  textAlign: 'left' as const,
  width: LABEL_COLUMN_WIDTH,
  minWidth: LABEL_COLUMN_WIDTH,
  maxWidth: LABEL_COLUMN_WIDTH,
};

export const VALUE_STYLE = {
  width: VALUE_COLUMN_WIDTH,
  minWidth: VALUE_COLUMN_WIDTH,
  maxWidth: VALUE_COLUMN_WIDTH,
};

export const LabelValueViewer = ({ label, tooltip }: { label: string; tooltip?: string }) => (
  // No fontSize here - inherits from the `.data-grid td` cell so the scoped size
  // override in NewRoundModeler.tsx (NRM_CELL_FONT_SIZE_SX) actually reaches this text.
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
    {/* title gives a native hover tooltip with the full text as a fallback for the rare
        label wider than the column (the column is sized for the common case, not every
        outlier); overflow/ellipsis keeps the cut genuinely "..." instead of a hard clip. */}
    <Box
      component="span"
      title={label}
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
      {label}
    </Box>
    {tooltip && (
      <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex' }}>
        <HelpTip title={tooltip} />
      </Box>
    )}
  </Box>
);

export const labelCell = (label: string, tooltip?: string) => ({
  readOnly: true,
  value: label,
  gridType: 'string' as const,
  style: LABEL_STYLE,
  valueViewer: () => <LabelValueViewer label={label} tooltip={tooltip} />,
});

// Read-only display cells. Mirrors the zero/absent-value "-" convention the original
// fmtNumber/fmtCurrency helpers used, since a genuine 0 (e.g. no unallocated option
// pool) reads the same as "no data yet" for these fields - only percent cells (which can
// legitimately be exactly 0%) skip that guard.
const isBlank = (value: number | null | undefined): boolean =>
  value == null || !isFinite(value) || value === 0;

export const blankCell = () => ({
  readOnly: true,
  value: EMPTY_DISPLAY_VALUE,
  gridType: 'string' as const,
  style: VALUE_STYLE,
});

export const numberCell = (value: number | null | undefined, decimals = 0) =>
  isBlank(value)
    ? blankCell()
    : {
      readOnly: true,
      value,
      gridType: 'number' as const,
      format: decimalFormat({ fractionDigits: decimals }),
      style: VALUE_STYLE,
    };

export const currencyCell = (value: number | null | undefined, currencyFormat: object) =>
  isBlank(value)
    ? blankCell()
    : {
      readOnly: true,
      value,
      gridType: 'number' as const,
      format: currencyFormat,
      style: VALUE_STYLE,
    };

export const percentCell = (value: number | null | undefined, percentFormat: object) =>
  value == null || !isFinite(value)
    ? blankCell()
    : {
      readOnly: true,
      value,
      gridType: 'percentage' as const,
      format: percentFormat,
      style: VALUE_STYLE,
    };
